/**
 * Debounce simple para el autoguardado (notas internas de la ficha del lead).
 * Reinicia el temporizador en cada llamada y dispara `fn` con los ÚLTIMOS
 * argumentos tras `ms` de inactividad. `flush` fuerza el guardado pendiente (al
 * cerrar el panel) y `cancel` lo descarta.
 */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  cancel: () => void;
  flush: () => void;
  pending: () => boolean;
}

export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;

  const run = (): void => {
    timer = null;
    if (lastArgs) {
      const args = lastArgs;
      lastArgs = null;
      fn(...args);
    }
  };

  const debounced = ((...args: A): void => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, ms);
  }) as Debounced<A>;

  debounced.cancel = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };

  debounced.flush = (): void => {
    if (timer) {
      clearTimeout(timer);
      run();
    }
  };

  debounced.pending = (): boolean => timer !== null;

  return debounced;
}
